import React from 'react';
import { Link } from 'react-router-dom';
import { TeamOutlined, ArrowRightOutlined } from '@ant-design/icons';

import { colorForBackgroundColor } from '../../utils/RGBColorToHex';

import { activeMemberCount } from './TeamRowList';

// The brand magenta, matching TitleColor's default on iOS: a team without its own
// colour still gets a coloured card rather than a grey one.
const DEFAULT_RGB = { r: 0xff, g: 0x00, b: 0x98 };

/** Accepts "#ff0098", "ff0098", "rgb(255,0,152)" or "255,0,152". */
export function parseTeamColor(rgbString) {
  if (!rgbString || typeof rgbString !== 'string') return DEFAULT_RGB;

  const hex = rgbString.trim().replace(/^#/, '');
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  const numbers = rgbString.match(/\d+/g);
  if (numbers && numbers.length >= 3) {
    const [r, g, b] = numbers.slice(0, 3).map((n) => Math.min(255, parseInt(n, 10)));
    return { r, g, b };
  }

  return DEFAULT_RGB;
}

/**
 * A team as a coloured tile, following the iOS home card: the team's colour as a
 * gradient, its logo fitted into a fixed-height banner so cards line up whatever
 * the logo's shape, the name, and an open action.
 */
export const TeamCardTile = ({ team, to, height = 240 }) => {
  const rgb = parseTeamColor(team.rgbColor);
  const base = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  // Use the app's own contrast rule rather than a local one, so a team card
  // agrees with every other surface that colours text against a brand colour.
  const text = colorForBackgroundColor(base);
  const fade = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`;
  // Shared with the row list rather than restated: the two displays disagreeing
  // about the same number is how this went wrong in the first place.
  const members = activeMemberCount(team);
  const onDark = text === 'white';

  const tile = (
    <div
      style={{
        height,
        borderRadius: 16,
        background: `linear-gradient(135deg, ${base} 0%, ${fade} 100%)`,
        color: text,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      }}
    >
      {/* Fixed-height banner: wide logos show wider, tall logos narrower, but the
          height never changes so a row of cards stays aligned. */}
      <div style={{ height: 72, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {team.topLogo
          ? <img src={team.topLogo} alt="" style={{ maxHeight: 72, maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
          : <TeamOutlined style={{ fontSize: 44, opacity: 0.85 }} />}
      </div>

      <div style={{ width: '100%', textAlign: 'center' }}>
        <div
          style={{ fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={team.teamName}
        >
          {team.teamName || 'Untitled team'}
        </div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
          {members === 1 ? '1 member' : `${members} members`}
        </div>
      </div>

      <div
        style={{
          width: '100%',
          textAlign: 'center',
          padding: '8px 12px',
          borderRadius: 999,
          fontWeight: 600,
          fontSize: 14,
          background: onDark ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.55)',
        }}
      >
        Open <ArrowRightOutlined style={{ fontSize: 12 }} />
      </div>
    </div>
  );

  return to
    ? <Link to={to} style={{ display: 'block', color: 'inherit' }}>{tile}</Link>
    : tile;
};

export default TeamCardTile;
